import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { endOfDay, startOfDay } from 'date-fns';
import { useMutation } from '@tanstack/react-query';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Messages } from 'primereact/messages';
import { Panel } from 'primereact/panel';
import { Calendar } from 'primereact/calendar';
import { SelectButton } from 'primereact/selectbutton';
import { DataTable as PrimeDataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { AppCard, AppPage } from '../../components/general';
import { toLocalDateTime } from '../../utils/date-helper';
import { TraceContextLogSummaryViewModel, SearchTraceLogRequest, AuwOrderHistorySearchResponse } from '../../models/auwTracing';
import { searchTraceLogs, getAuwOrderHistoryDetail } from '../../services/auwTracingService';
import { useAuwOrderHistorySearch } from '../../hooks/useAuwTracing';
import styles from './AuwTracingSearch.module.css';

type AuwSearchMode = 'traceLogs' | 'auwOrder';

const searchModeOptions = [
  { label: 'Trace Logs', value: 'traceLogs' },
  { label: 'AUW Orders', value: 'auwOrder' },
];

const AuwTracingSearch = () => {
  const navigate = useNavigate();
  const messages = useRef<any>(null);

  const [searchMode, setSearchMode] = useState<AuwSearchMode>('auwOrder');

  // Direct search
  const [directId, setDirectId] = useState('');
  const [directSearchLoading, setDirectSearchLoading] = useState(false);

  // Common fields
  const [loanNumber, setLoanNumber] = useState<string>('');
  const [loanIdentifier, setLoanIdentifier] = useState<string>('');

  // Trace log specific
  const [advancedSearchExpanded, setAdvancedSearchExpanded] = useState<boolean>(false);
  const now = new Date();
  const start = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
  const end = endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  const [startDate, setStartDate] = useState<Date | null>(start);
  const [endDate, setEndDate] = useState<Date | null>(end);

  // AUW Order specific
  const [auwOrderId, setAuwOrderId] = useState('');

  // Search state
  const [auwSearchEnabled, setAuwSearchEnabled] = useState(false);
  const [auwSearchParams, setAuwSearchParams] = useState<any>({});

  const { searchQuery: auwOrderQuery } = useAuwOrderHistorySearch(auwSearchParams, auwSearchEnabled);

  const traceLogSearchMutation = useMutation<TraceContextLogSummaryViewModel[], Error, SearchTraceLogRequest>({
    mutationFn: (params: SearchTraceLogRequest) => searchTraceLogs('AUW', params),
  });

  const handleDirectSearch = useCallback(async () => {
    if (!directId || directId.trim().length === 0) {
      messages.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: `Please enter ${searchMode === 'auwOrder' ? 'an AUW Order ID' : 'a Transaction ID'}`,
        life: 3000,
      });
      return;
    }

    const id = directId.trim();
    setDirectSearchLoading(true);
    try {
      if (searchMode === 'auwOrder') {
        await getAuwOrderHistoryDetail(id);
        navigate(`/auw-tracing/order/${id}`);
      } else {
        navigate(`/auw-tracing/${id}`);
      }
    } catch (error) {
      messages.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error instanceof Error ? error.message : 'Failed to load',
        life: 5000,
      });
    } finally {
      setDirectSearchLoading(false);
    }
  }, [directId, searchMode, navigate]);

  const handleSearch = () => {
    if (searchMode === 'traceLogs') {
      const params: SearchTraceLogRequest = Object.fromEntries(
        Object.entries({
          loanNumber: loanNumber.trim() || null,
          loanIdentifier: loanIdentifier.trim() || null,
          startDate: startDate && endDate ? startOfDay(startDate).toISOString() : null,
          endDate: startDate && endDate ? endOfDay(endDate).toISOString() : null,
        }).filter(([, v]) => v != null)
      );
      traceLogSearchMutation.mutate(params);
      setAuwSearchEnabled(false);
    } else {
      const params: any = {};
      if (loanNumber.trim()) params.loanNumber = loanNumber.trim();
      if (loanIdentifier.trim()) params.loanIdentifier = loanIdentifier.trim();
      if (auwOrderId.trim()) params.auwOrderId = auwOrderId.trim();

      setAuwSearchParams(params);
      setAuwSearchEnabled(true);
    }
  };

  const handleModeChange = (value: AuwSearchMode) => {
    if (value) {
      setSearchMode(value);
      setAuwSearchEnabled(false);
    }
  };

  const [selectedTrace, setSelectedTrace] = useState<TraceContextLogSummaryViewModel | null>(null);

  const handleTraceSelectionChange = (e: any) => {
    const selected = e.value as TraceContextLogSummaryViewModel;
    if (selected?.transactionId) {
      navigate(`/auw-tracing/${selected.transactionId}`);
    }
  };

  const handleAuwOrderSelectionChange = (e: any) => {
    const selected = e.value as AuwOrderHistorySearchResponse;
    if (selected?.auwOrderHistoryGuid) {
      navigate(`/auw-tracing/order/${selected.auwOrderHistoryGuid}`);
    }
  };

  const formatDateTime = (rowData: { eventTimestamp?: string; createdDate?: string }) => {
    const ts = rowData.eventTimestamp || rowData.createdDate;
    return ts ? toLocalDateTime(ts) : '';
  };

  const getStatusSeverity = (status: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'completed':
        return 'success';
      case 'pending':
      case 'inprogress':
      case 'awaitingcallback':
        return 'info';
      case 'failed':
      case 'error':
        return 'danger';
      case 'canceled':
      case 'timeout':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const statusBodyTemplate = (rowData: AuwOrderHistorySearchResponse) => {
    return <Tag value={rowData.status} severity={getStatusSeverity(rowData.status)} />;
  };

  const isSearchActive = searchMode === 'traceLogs'
    ? (traceLogSearchMutation.data || traceLogSearchMutation.isPending || traceLogSearchMutation.isError)
    : (auwOrderQuery.data || auwOrderQuery.isFetching || auwOrderQuery.isError);

  return (
    <AppPage title="AUW Transaction Logs" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'AUW', to: '/products/auw' }, { label: 'Transaction Logs' }]}>
      <Messages ref={messages} />
      
      <AppCard className={styles.auwTracingSearch}>
        <div className="flex flex-column gap-4">
          <SelectButton
            value={searchMode}
            onChange={(e) => handleModeChange(e.value)}
            options={searchModeOptions}
            className="mb-2"
          />

          <div className="flex flex-column gap-2">
            <label htmlFor="directId" className="font-semibold">
              {searchMode === 'auwOrder' ? 'Search by AUW Order ID' : 'Search by Transaction ID'}
            </label>
            <div className="flex gap-2">
              <InputText
                id="directId"
                value={directId}
                onChange={(e) => setDirectId(e.target.value)}
                placeholder={searchMode === 'auwOrder' ? 'Paste AUW Order GUID here...' : 'Paste transaction ID here...'}
                className="flex-1"
                style={{ backgroundColor: '#f9fafb' }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleDirectSearch();
                  }
                }}
              />
              <Button
                label="Go"
                icon="pi pi-search"
                onClick={handleDirectSearch}
                disabled={!directId || directId.trim().length === 0}
                loading={directSearchLoading}
              />
            </div>
          </div>

          <Panel
            header="Advanced Search"
            toggleable
            collapsed={!advancedSearchExpanded}
            onToggle={(e) => setAdvancedSearchExpanded(!e.value)}
          >
            <div className="flex flex-column" style={{ gap: '1rem' }}>
              <div className="flex flex-column gap-2">
                <label htmlFor="loanNumber">Loan Number</label>
                <InputText
                  id="loanNumber"
                  value={loanNumber}
                  onChange={(e) => setLoanNumber(e.target.value)}
                  placeholder="Enter loan number"
                  style={{ backgroundColor: '#f9fafb' }}
                />
              </div>

              <div className="flex flex-column gap-2">
                <label htmlFor="loanIdentifier">Loan Identifier</label>
                <InputText
                  id="loanIdentifier"
                  value={loanIdentifier}
                  onChange={(e) => setLoanIdentifier(e.target.value)}
                  placeholder="Enter loan identifier"
                  style={{ backgroundColor: '#f9fafb' }}
                />
              </div>

              {searchMode === 'auwOrder' && (
                <div className="flex flex-column gap-2">
                  <label htmlFor="auwOrderId">AUW Order ID</label>
                  <InputText
                    id="auwOrderId"
                    value={auwOrderId}
                    onChange={(e) => setAuwOrderId(e.target.value)}
                    placeholder="Enter AUW Order GUID"
                    style={{ backgroundColor: '#f9fafb' }}
                  />
                </div>
              )}

              {searchMode === 'traceLogs' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-column gap-2">
                    <label htmlFor="startDate">Start Date</label>
                    <Calendar
                      id="startDate"
                      value={startDate}
                      onChange={(e) => setStartDate(e.value as Date)}
                      showIcon
                      dateFormat="mm/dd/yy"
                      placeholder="Select start date"
                      inputStyle={{ backgroundColor: '#f9fafb' }}
                      iconPos="right"
                    />
                  </div>
                  
                  <div className="flex flex-column gap-2">
                    <label htmlFor="endDate">End Date</label>
                    <Calendar
                      id="endDate"
                      value={endDate}
                      onChange={(e) => setEndDate(e.value as Date)}
                      showIcon
                      dateFormat="mm/dd/yy"
                      placeholder="Select end date"
                      inputStyle={{ backgroundColor: '#f9fafb' }}
                      iconPos="right"
                    />
                  </div>
                </div>
              )}

              <div className="mt-2">
                <Button
                  label="Search"
                  icon="pi pi-search"
                  onClick={handleSearch}
                />
              </div>
            </div>
          </Panel>

          {isSearchActive && (
            <div className="flex flex-column gap-2">
              <h3 className="font-semibold text-lg">Search Results</h3>

              {searchMode === 'traceLogs' && (
                <>
                  {traceLogSearchMutation.isPending && <div>Loading...</div>}
                  {traceLogSearchMutation.isError && (
                    <div className="text-red-500">
                      Error: {traceLogSearchMutation.error instanceof Error ? traceLogSearchMutation.error.message : 'Unknown error'}
                    </div>
                  )}
                  {traceLogSearchMutation.data && (
                    <PrimeDataTable
                      value={traceLogSearchMutation.data}
                      selectionMode="single"
                      selection={selectedTrace}
                      onSelectionChange={handleTraceSelectionChange}
                      paginator
                      rows={20}
                      emptyMessage="No results found"
                      className="cursor-pointer"
                    >
                      <Column field="eventTimestamp" header="DATE & TIME" body={formatDateTime} sortable />
                      <Column field="transactionId" header="TRANSACTION ID" sortable />
                      <Column field="loanNumber" header="LOAN NUMBER" sortable />
                      <Column field="loanIdentifier" header="LOAN IDENTIFIER" sortable />
                    </PrimeDataTable>
                  )}
                </>
              )}

              {searchMode === 'auwOrder' && (
                <>
                  {auwOrderQuery.isFetching && <div>Loading...</div>}
                  {auwOrderQuery.isError && (
                    <div className="text-red-500">
                      Error: {auwOrderQuery.error instanceof Error ? auwOrderQuery.error.message : 'Unknown error'}
                    </div>
                  )}
                  {auwOrderQuery.data && !auwOrderQuery.isFetching && (
                    <PrimeDataTable
                      value={auwOrderQuery.data}
                      selectionMode="single"
                      onSelectionChange={handleAuwOrderSelectionChange}
                      paginator
                      rows={20}
                      emptyMessage="No results found"
                      className="cursor-pointer"
                    >
                      <Column field="createdDate" header="DATE" body={formatDateTime} sortable />
                      <Column field="auwOrderHistoryGuid" header="ORDER ID" sortable />
                      <Column field="transactionName" header="TRANSACTION" sortable />
                      <Column field="integrationVendor" header="VENDOR" sortable />
                      <Column field="loanNumber" header="LOAN NUMBER" sortable />
                      <Column field="status" header="STATUS" body={statusBodyTemplate} sortable />
                    </PrimeDataTable>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </AppCard>
    </AppPage>
  );
};

export default AuwTracingSearch;
